import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconCopy, IconEye, IconPlus, IconTrash } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import type { OrderListItem, OrderStatus } from '@expeditor/shared';
import { ordersApi } from '../../shared/api/orders';
import {
  ORDER_STATUS_LABEL,
  orderStatusColor,
  orderStatusLabel,
  transportLabel,
} from '../../shared/ui/order-labels';

export function MyOrdersPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<OrderListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      setError(null);
      const data = await ordersApi.list(statusFilter ? { status: statusFilter } : {});
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить список');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const onClone = async (id: string) => {
    try {
      const copy = await ordersApi.clone(id);
      nav(`/cabinet/orders/${copy.id}/edit`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось скопировать');
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Удалить черновик безвозвратно?')) return;
    try {
      await ordersApi.remove(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось удалить');
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Мои поручения</Title>
        <Group>
          <Select
            placeholder="Все статусы"
            data={Object.entries(ORDER_STATUS_LABEL).map(([v, l]) => ({ value: v, label: l }))}
            value={statusFilter || null}
            onChange={(v) => setStatusFilter((v as OrderStatus) || '')}
            clearable
            w={180}
          />
          <Button
            component={Link}
            to="/cabinet/orders/new"
            leftSection={<IconPlus size={16} />}
          >
            Новое поручение
          </Button>
        </Group>
      </Group>

      {error && <Text c="red">{error}</Text>}

      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Номер</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th>Груз</Table.Th>
            <Table.Th>Вес, кг</Table.Th>
            <Table.Th>Транспорт</Table.Th>
            <Table.Th>Создано</Table.Th>
            <Table.Th style={{ width: 130 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((o) => (
            <Table.Tr key={o.id}>
              <Table.Td>
                <Text fw={500} c="blue" component={Link} to={`/cabinet/orders/${o.id}`}>
                  {o.number}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge color={orderStatusColor(o.status)}>{orderStatusLabel(o.status)}</Badge>
              </Table.Td>
              <Table.Td>{o.cargoName}</Table.Td>
              <Table.Td>{o.cargoWeightKg}</Table.Td>
              <Table.Td>{transportLabel(o.transportMode)}</Table.Td>
              <Table.Td>{new Date(o.createdAt).toLocaleString('ru-RU')}</Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label="Просмотр">
                    <ActionIcon
                      variant="subtle"
                      component={Link}
                      to={`/cabinet/orders/${o.id}`}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Создать копию">
                    <ActionIcon variant="subtle" onClick={() => onClone(o.id)}>
                      <IconCopy size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {o.status === 'DRAFT' && (
                    <Tooltip label="Удалить черновик">
                      <ActionIcon variant="subtle" color="red" onClick={() => onDelete(o.id)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed" ta="center" py="md">
                  Пока нет поручений
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
